import {
  Box,
  Button,
  Checkbox,
  chakra,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Image,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from "@chakra-ui/react";
import React from "react";
import { FiUpload } from "react-icons/fi";
import SignatureCanvas from "../../servicePlan/components/SignatureCanvas";
import { answerAsString } from "./answerFormat";
import { canSubmitWrites, type AnswerWrite } from "./answerWrites";
import { EditorShell } from "./EditorShell";
import { isAnswerChanged, useAnswerWriter, useEditorDraft } from "./editorValues";
import {
  customSignatureNameQuestion,
  unsupportedStoredAnswerNote,
  initialSignatureDraft,
  isSignatureItemType,
  setConsent,
  setNotSigning,
  signatureAnswerFromDraft,
  signatureOptions,
  type PatientNotSigningChoice,
  type SignatureDraft,
  type SignatureItemType,
} from "./signatureDraft";
import {
  classifySignatureAnswer,
  PATIENT_NOT_SIGNING_OTHER,
  PATIENT_NOT_SIGNING_REASONS,
} from "./signatureValue";
import {
  aspectFitRect,
  SIGNATURE_IMAGE_HEIGHT,
  SIGNATURE_IMAGE_WIDTH,
  signatureUploadError,
} from "./signatureImage";
import type { QuestionEditorProps } from "./types";

// Editor of employeeSignature, patientSignature and customSignature: the signature (pad, or the
// stored / uploaded image with "Clear to re-sign") with Upload, then the primary button "Sign"
// saves it (a cleared pad saves ""; an upload is stored as a pad-sized PNG). The
// not-signing and verbal-consent controls (signatureDraft.ts) save their text or marker with
// "Save". customSignature also edits its name child ("<label> - Full Name"), a separate question
// written before the signature, both through `onSaveAnswers` (Retry sends what is left).

const PATIENT_REASON_CHOICES: PatientNotSigningChoice[] = [
  ...PATIENT_NOT_SIGNING_REASONS,
  PATIENT_NOT_SIGNING_OTHER,
];

/** Decodes the file and redraws it, aspect-fit, on a pad-sized canvas: a PNG data URL. */
function normalizeSignatureImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new window.Image();
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = SIGNATURE_IMAGE_WIDTH;
        canvas.height = SIGNATURE_IMAGE_HEIGHT;
        const context = canvas.getContext("2d");
        if (context === null) {
          throw new Error("Canvas is not available");
        }
        const rect = aspectFitRect(
          { width: image.naturalWidth, height: image.naturalHeight },
          { width: SIGNATURE_IMAGE_WIDTH, height: SIGNATURE_IMAGE_HEIGHT }
        );
        context.drawImage(image, rect.x, rect.y, rect.width, rect.height);
        resolve(canvas.toDataURL());
      } catch (error) {
        reject(error);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not decode the image"));
    };
    image.src = url;
  });
}

/**
 * The signature: a stored or uploaded one shows read-only with "Clear to re-sign" (drawing over it
 * on the pad would redraw it rescaled to the pad, degrading it), the pad only once cleared.
 */
function SignaturePadField(props: {
  isSaving: boolean;
  signature: string;
  onChange: (signature: string) => void;
}) {
  const { isSaving, onChange, signature } = props;
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isPadOpen, setIsPadOpen] = React.useState(signature === "");
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  // A drawn signature being saved shows as the image it is saved as, and stays so afterwards (a
  // failed save keeps it for Retry without redrawing it on a new pad)
  const isSavingDrawing = isSaving && signature !== "";
  React.useEffect(() => {
    if (isSavingDrawing) {
      setIsPadOpen(false);
    }
  }, [isSavingDrawing]);
  const showsPad = isPadOpen && !isSavingDrawing;

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Lets the same file be picked again
    event.target.value = "";
    if (file === undefined) {
      return;
    }
    const refusal = signatureUploadError(file);
    if (refusal !== null) {
      setUploadError(refusal);
      return;
    }
    try {
      const dataUrl = await normalizeSignatureImage(file);
      setUploadError(null);
      onChange(dataUrl);
      setIsPadOpen(false);
    } catch {
      setUploadError("Could not read the image. Choose a PNG or JPEG file.");
    }
  };

  const clear = () => {
    setUploadError(null);
    onChange("");
    setIsPadOpen(true);
  };

  return (
    <Stack spacing={2}>
      {showsPad ? (
        <Box maxW="100%" overflowX="auto">
          <SignatureCanvas
            isDisabled={isSaving}
            value={null}
            onChange={(dataUrl) => {
              setUploadError(null);
              onChange(dataUrl ?? "");
            }}
          />
        </Box>
      ) : (
        <HStack align="flex-end" spacing={3}>
          <Flex
            align="center"
            borderRadius="md"
            borderWidth="1px"
            h={`${SIGNATURE_IMAGE_HEIGHT}px`}
            justify="center"
            maxW="100%"
            w={`${SIGNATURE_IMAGE_WIDTH}px`}
          >
            <Image alt="Signature" maxH="100%" maxW="100%" src={signature} />
          </Flex>
          <Button isDisabled={isSaving} size="sm" variant="ghost" onClick={clear}>
            Clear to re-sign
          </Button>
        </HStack>
      )}
      <Box>
        {/* Inline style: the AngularJS page's input[type="file"] rule outranks a class */}
        <chakra.input
          ref={fileInputRef}
          accept="image/*"
          style={{ display: "none" }}
          type="file"
          onChange={(event) => void handleFile(event)}
        />
        <Button
          isDisabled={isSaving}
          leftIcon={<FiUpload />}
          size="sm"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
        >
          Upload
        </Button>
      </Box>
      {uploadError !== null && (
        <Text color="red.600" fontSize="xs">
          {uploadError}
        </Text>
      )}
    </Stack>
  );
}

function PatientNotSigningFields(props: {
  draft: SignatureDraft;
  offersConsent: boolean;
  onChange: (update: (prev: SignatureDraft) => SignatureDraft) => void;
}) {
  const { draft, onChange } = props;
  const hasConsent = draft.mode === "consent";

  return (
    <Stack pl={6} spacing={2}>
      {!hasConsent && (
        <RadioGroup
          value={draft.reason ?? ""}
          onChange={(value) =>
            onChange((prev) => ({ ...prev, reason: value as PatientNotSigningChoice }))
          }
        >
          <Stack spacing={1}>
            {PATIENT_REASON_CHOICES.map((choice) => (
              <Radio key={choice} size="sm" value={choice}>
                {choice}
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      )}
      {!hasConsent && (
        <Input
          aria-label="Other reason"
          placeholder="Other reason"
          size="sm"
          value={draft.otherText}
          onChange={(event) => {
            const otherText = event.target.value;
            // Typing the text selects "Other" (mobile)
            onChange((prev) => ({ ...prev, reason: PATIENT_NOT_SIGNING_OTHER, otherText }));
          }}
        />
      )}
      {props.offersConsent && (
        <Checkbox
          isChecked={hasConsent}
          size="sm"
          onChange={(event) => {
            const isChecked = event.target.checked;
            onChange((prev) => setConsent("patientSignature", prev, isChecked));
          }}
        >
          Patient approves digital signature
        </Checkbox>
      )}
    </Stack>
  );
}

function SignatureEditor(props: QuestionEditorProps & { itemType: SignatureItemType }) {
  const { itemType, question } = props;
  const options = signatureOptions(question, itemType, props.value);
  const {
    draft,
    isTouched,
    updateDraft: setDraft,
  } = useEditorDraft(() => initialSignatureDraft(itemType, props.value));
  const writer = useAnswerWriter(props.onSaveAnswers);
  const updateDraft: typeof setDraft = (update) => {
    writer.reset();
    setDraft(update);
  };

  // customSignature's name child, edited here unless hidden. In a chart cell `answers` holds the
  // row's answers only, so this is the name of the same row.
  const nameQuestion =
    itemType === "customSignature" ? customSignatureNameQuestion(question) : null;
  const nameAnswer = nameQuestion === null ? undefined : props.answers.get(nameQuestion.id)?.[0];
  const storedName = answerAsString(nameAnswer?.answer) ?? "";
  const showsName = nameQuestion !== null && question.hideNameFieldSignature !== true;
  const [name, setName] = React.useState(storedName);
  const [isNameTouched, setIsNameTouched] = React.useState(false);

  const next = signatureAnswerFromDraft(itemType, draft);
  const isSignatureChanged = isTouched && next !== null && isAnswerChanged(props.value, next);
  const isNameChanged = showsName && isNameTouched && isAnswerChanged(storedName, name);
  // Verbal consent prints the signee's name as the signature (mobile requires it then)
  const needsName = showsName && draft.mode === "consent" && name.trim() === "";
  const canSave = next !== null && !needsName && (isSignatureChanged || isNameChanged);

  const handleSave = async () => {
    const writes: AnswerWrite[] = [];
    if (isNameChanged && nameQuestion !== null) {
      writes.push({
        questionId: nameQuestion.id,
        chartRow: nameAnswer?.chartRow ?? props.chartRow,
        answer: name,
      });
    }
    if (isSignatureChanged && next !== null) {
      writes.push({ questionId: question.id, chartRow: props.chartRow, answer: next });
    }
    // A failure shows through `error`; Retry sends the writes that are left
    if (await writer.run(writes)) {
      props.onClose();
    }
  };

  const storedNote = unsupportedStoredAnswerNote(
    itemType,
    classifySignatureAnswer(props.value).kind
  );

  return (
    <EditorShell
      canSave={canSubmitWrites(canSave, writer.pending)}
      error={props.error}
      isSaving={props.isSaving}
      label={question.label}
      saveLabel={draft.mode === "sign" && isSignatureChanged ? "Sign" : "Save"}
      onCancel={props.onCancel}
      onSave={() => void handleSave()}
    >
      <Stack spacing={3}>
        {showsName && (
          <FormControl>
            <FormLabel fontSize="sm" mb={1}>
              {question.label} - Full Name
            </FormLabel>
            <Input
              size="sm"
              value={name}
              onChange={(event) => {
                writer.reset();
                setIsNameTouched(true);
                setName(event.target.value);
              }}
            />
          </FormControl>
        )}

        {draft.mode === "sign" && (
          <SignaturePadField
            isSaving={props.isSaving}
            signature={draft.signature}
            onChange={(signature) => updateDraft((prev) => ({ ...prev, signature }))}
          />
        )}

        {storedNote !== null && !isTouched && (
          <Text color="orange.600" fontSize="xs">
            {storedNote}
          </Text>
        )}

        {options.consent === "standalone" && (
          <Checkbox
            isChecked={draft.mode === "consent"}
            size="sm"
            onChange={(event) => {
              const isChecked = event.target.checked;
              updateDraft((prev) => setConsent(itemType, prev, isChecked));
            }}
          >
            Signee gave verbal consent
          </Checkbox>
        )}

        {options.notSigning && (
          <Checkbox
            isChecked={
              itemType === "patientSignature" ? draft.mode !== "sign" : draft.mode === "notSigning"
            }
            size="sm"
            onChange={(event) => {
              const isChecked = event.target.checked;
              updateDraft((prev) => setNotSigning(prev, isChecked));
            }}
          >
            {itemType === "patientSignature" ? "Patient is not signing" : "Not signing"}
          </Checkbox>
        )}

        {itemType === "patientSignature" && draft.mode !== "sign" && (
          <PatientNotSigningFields
            draft={draft}
            offersConsent={options.consent === "underNotSigning"}
            onChange={updateDraft}
          />
        )}

        {itemType !== "patientSignature" && draft.mode === "notSigning" && (
          <Input
            aria-label="Name"
            placeholder="Name"
            size="sm"
            value={draft.notSigningName}
            onChange={(event) => {
              const notSigningName = event.target.value;
              updateDraft((prev) => ({ ...prev, notSigningName }));
            }}
          />
        )}

        {needsName && (
          <Text color="gray.600" fontSize="xs">
            Enter the signee&apos;s full name: with verbal consent the PDF prints it as the
            signature.
          </Text>
        )}
      </Stack>
    </EditorShell>
  );
}

export function SignatureQuestionEditor(props: QuestionEditorProps) {
  const { itemType } = props.question;
  if (!isSignatureItemType(itemType)) {
    return null;
  }
  return <SignatureEditor {...props} itemType={itemType} />;
}
